import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import logo from '../../assets/images/logo.png';
import { useAuth } from '../../hooks/useAuth';
import { useSubscriptionStore } from '../../stores/subscriptionStore';
import AvatarPicker from './AvatarPicker';
import UserAvatar from './UserAvatar';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === '/';
  const { isAuthenticated, user, logout } = useAuth();
  const { currentSubscription, loaded, loadCurrentSubscription } = useSubscriptionStore();

  useEffect(() => {
    if (isAuthenticated && !loaded) {
      loadCurrentSubscription();
    }
  }, [isAuthenticated, loaded, loadCurrentSubscription]);

  const PLAN_LABELS: Record<string, string> = {
    unlimited: 'ULT',
    group: 'GRP',
    free: 'Free',
  };
  const planCode = currentSubscription?.plan_detail?.code ?? currentSubscription?.plan ?? null;
  const planLabel = planCode ? (PLAN_LABELS[planCode] ?? planCode.toUpperCase()) : null;

  const handleLogout = () => {
    logout();
    navigate('/');
    setMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Recursos', href: '/#features' },
    { label: 'Planos', href: '/plans' },
    { label: 'Colaboração', href: '/collab' },
  ];

  const navBg = !isHome || scrolled || menuOpen
    ? 'bg-[#0A0A0F]/95 backdrop-blur-md border-b border-white/5'
    : 'bg-transparent';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navBg}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src={logo}
            alt="Escala Equipe Logo"
            className="w-8 h-8 object-contain"
          />
          <span className="text-white font-bold text-lg tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
            Escala Equipe
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="text-white/60 hover:text-white text-sm uppercase tracking-widest transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {link.label}
            </Link>
          ))}
        </div>

        {/* Auth Buttons */}
        <div className="hidden md:flex items-center gap-3">
          {isAuthenticated ? (
            <>
              <Link
                to="/teams"
                className="text-white/70 hover:text-white text-sm uppercase tracking-widest transition-colors whitespace-nowrap cursor-pointer"
              >
                Equipes
              </Link>
              <Link
                to="/events"
                className="text-white/70 hover:text-white text-sm uppercase tracking-widest transition-colors whitespace-nowrap cursor-pointer"
              >
                Eventos
              </Link>
              <Link
                to="/schedule"
                className="text-white/70 hover:text-white text-sm uppercase tracking-widest transition-colors whitespace-nowrap cursor-pointer"
              >
                Escala
              </Link>
              <Link
                to="/library"
                className="text-white/70 hover:text-white text-sm uppercase tracking-widest transition-colors whitespace-nowrap cursor-pointer"
              >
                Biblioteca
              </Link>
              <Link
                to="/preferences"
                aria-label="Preferências"
                title="Preferências"
                className="flex h-9 w-9 items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white"
              >
                <i className="ri-settings-3-line text-lg" aria-hidden="true" />
              </Link>
              {isAuthenticated && planLabel && (
                <Link
                  to="/subscription-dashboard"
                  className="flex items-center gap-1.5 bg-[#C8A96E]/15 hover:bg-[#C8A96E]/25 border border-[#C8A96E]/30 text-[#C8A96E] text-xs font-semibold px-3 py-1.5 rounded-full transition-colors whitespace-nowrap cursor-pointer"
                  title="Meu plano"
                >
                  <span>Meu plano</span>
                  <span className="bg-[#C8A96E] text-[#0A0A0F] text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{planLabel}</span>
                </Link>
              )}
              <button
                onClick={() => setAvatarPickerOpen(true)}
                className="cursor-pointer rounded-full hover:ring-2 hover:ring-[#C8A96E]/50 transition-all"
                title="Alterar avatar"
              >
                <UserAvatar avatarUrl={user?.avatar_url} name={user?.name ?? ''} size="sm" />
              </button>
              <button
                onClick={handleLogout}
                className="bg-white/10 text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                Sair
              </button>
            </>
          ) : (
            <>
              <Link
                to="/auth"
                className="text-white/70 hover:text-white text-sm uppercase tracking-widest transition-colors whitespace-nowrap cursor-pointer"
              >
                Entrar
              </Link>
              <Link
                to="/auth?mode=register"
                className="bg-white text-[#0A0A0F] text-sm font-semibold px-5 py-2 rounded-full hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                Começar grátis
              </Link>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden text-white w-8 h-8 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-xl`}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0A0A0F]/98 border-t border-white/10 px-6 py-6 flex flex-col gap-5">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.href}
              className="text-white/70 hover:text-white text-sm uppercase tracking-widest cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </Link>
          ))}
          <div className="flex flex-col gap-3 pt-4 border-t border-white/10">
            {isAuthenticated ? (
              <>
                <Link
                  to="/teams"
                  className="text-white/70 text-sm uppercase tracking-widest cursor-pointer"
                  onClick={() => setMenuOpen(false)}
                >
                  Equipes
                </Link>
                <Link
                  to="/events"
                  className="text-white/70 text-sm uppercase tracking-widest cursor-pointer"
                  onClick={() => setMenuOpen(false)}
                >
                  Eventos
                </Link>
                <Link
                  to="/schedule"
                  className="text-white/70 text-sm uppercase tracking-widest cursor-pointer"
                  onClick={() => setMenuOpen(false)}
                >
                  Escala
                </Link>
                <Link
                  to="/library"
                  className="text-white/70 text-sm uppercase tracking-widest cursor-pointer"
                  onClick={() => setMenuOpen(false)}
                >
                  Biblioteca
                </Link>
                <Link
                  to="/preferences"
                  className="text-white/70 text-sm uppercase tracking-widest cursor-pointer"
                  onClick={() => setMenuOpen(false)}
                >
                  Preferências
                </Link>
                {isAuthenticated && planLabel && (
                  <Link
                    to="/subscription-dashboard"
                    className="flex items-center gap-2 text-[#C8A96E] text-sm cursor-pointer"
                    onClick={() => setMenuOpen(false)}
                  >
                    Meu plano
                    <span className="bg-[#C8A96E] text-[#0A0A0F] text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{planLabel}</span>
                  </Link>
                )}
                {user?.name && <span className="text-white/40 text-sm">{user.name}</span>}
                <button onClick={handleLogout} className="bg-white/10 text-white text-sm font-semibold px-5 py-2 rounded-full text-center cursor-pointer">Sair</button>
              </>
            ) : (
              <>
                <Link to="/auth" className="text-white/70 text-sm uppercase tracking-widest cursor-pointer" onClick={() => setMenuOpen(false)}>Entrar</Link>
                <Link to="/auth?mode=register" className="bg-white text-[#0A0A0F] text-sm font-semibold px-5 py-2 rounded-full text-center cursor-pointer" onClick={() => setMenuOpen(false)}>Começar grátis</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
    {avatarPickerOpen && <AvatarPicker onClose={() => setAvatarPickerOpen(false)} />}
    </>
  );
};

export default Navbar;
